import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export interface NotificationRow {
  id: string;
  eventType: string;
  title: string;
  body: string;
  subjectType: string | null;
  subjectId: string | null;
  payload: Record<string, unknown>;
  actorId: string | null;
  actorName: string | null;
  readAt: string | null;
  createdAt: string | null;
}

export interface NotificationListResponse {
  results: NotificationRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

const listKey = (params: object) => ["notifications", "list", params] as const;
const unreadKey = ["notifications", "unread-count"] as const;

export function useNotifications(params: { unread?: boolean; eventType?: string } = {}) {
  return useQuery<NotificationListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<NotificationListResponse>("/notifications", {
          params: {
            unread: params.unread ? "true" : undefined,
            eventType: params.eventType || undefined,
          },
        })
        .then((r) => r.data),
  });
}

export function useUnreadCount() {
  return useQuery<{ count: number }>({
    queryKey: unreadKey,
    queryFn: () => api.get<{ count: number }>("/notifications/unread-count").then((r) => r.data),
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<NotificationRow>(`/notifications/${id}/read`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ count: number }>("/notifications/mark-all-read").then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
